import router from '@/router';
import store from '@/store';
import { cancelHighlightCheckout } from '@/api/partner';
import { notifyPartnerStatusChanged } from '@/utils/partnerMembership';
import {
  readStripeEmbeddedCheckoutContext,
  clearStripeEmbeddedCheckoutContext,
  STRIPE_POLL_KIND,
} from '@/utils/stripeEmbeddedCheckout';
import { cleanupPartnerEmbeddedCheckoutCancel } from '@/utils/openStripeEmbeddedCheckout';
import {
  consumePartnerStripeCheckoutKind,
  consumePartnerStripeCheckoutStarted,
  getPartnerCheckoutQuery,
  peekPartnerStripeCheckoutKind,
  peekPartnerStripeCheckoutStarted,
} from '@/utils/partnerCheckoutReturn';

const STALE_HIGHLIGHT_RECONCILE_KEY = 'partner:staleHighlightPendingReconciled';

let reconcileInFlight = null;

const readLocationSessionId = () => {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('session_id') || '';
};

const hasActiveCheckoutReturn = (activeRouter) => {
  const route = activeRouter.currentRoute?.value;
  const query = getPartnerCheckoutQuery(activeRouter);
  if (query?.checkout) return true;

  const sessionId = route?.query?.session_id || readLocationSessionId();
  if (sessionId) return true;

  if (route?.name === 'navigation.checkout-return') {
    return true;
  }
  return false;
};

const reconcileStaleServerHighlightPending = async () => {
  if (sessionStorage.getItem(STALE_HIGHLIGHT_RECONCILE_KEY) === '1') {
    return false;
  }

  if (!store.getters['profile/getCompanyID']) {
    return false;
  }

  await store.dispatch('partner/refreshStatus');
  const status = store.getters['partner/getStatus'];
  const pending = status?.highlightPending;
  if (pending?.status !== 'pending' || pending?.source !== 'stripe_checkout') {
    return false;
  }

  sessionStorage.setItem(STALE_HIGHLIGHT_RECONCILE_KEY, '1');
  try {
    const result = await cancelHighlightCheckout();
    await store.dispatch('partner/refreshStatus');
    notifyPartnerStatusChanged({ status: store.getters['partner/getStatus'] });
    return Boolean(result?.cancelled);
  } catch (err) {
    console.warn('Stale partner highlight pending cleanup failed', err);
    sessionStorage.removeItem(STALE_HIGHLIGHT_RECONCILE_KEY);
    return false;
  }
};

const reconcileHostedHighlightAbandon = async () => {
  if (peekPartnerStripeCheckoutKind() !== STRIPE_POLL_KIND.PARTNER_HIGHLIGHT) {
    return false;
  }
  if (!peekPartnerStripeCheckoutStarted()) {
    return false;
  }

  consumePartnerStripeCheckoutStarted();
  consumePartnerStripeCheckoutKind();

  try {
    await cancelHighlightCheckout();
    await store.dispatch('partner/refreshStatus');
    notifyPartnerStatusChanged({ status: store.getters['partner/getStatus'] });
    return true;
  } catch (err) {
    console.warn('Hosted highlight checkout abandon cleanup failed', err);
    return false;
  }
};

/**
 * Embedded modal closed via kill / iOS "Fertig" without cancel_url, or stale pending rows
 * after sessionStorage was cleared. Does not run during an active Stripe return URL flow.
 */
export async function reconcileAbandonedPartnerCheckout(activeRouter = router) {
  if (reconcileInFlight) {
    return reconcileInFlight;
  }

  if (hasActiveCheckoutReturn(activeRouter)) {
    return false;
  }

  reconcileInFlight = (async () => {
    const embeddedContext = readStripeEmbeddedCheckoutContext();
    if (embeddedContext) {
      await cleanupPartnerEmbeddedCheckoutCancel(
        embeddedContext.pollKind,
        embeddedContext.sessionId,
      );
      clearStripeEmbeddedCheckoutContext();
      return true;
    }

    if (await reconcileHostedHighlightAbandon()) {
      return true;
    }

    return reconcileStaleServerHighlightPending();
  })();

  try {
    return await reconcileInFlight;
  } finally {
    reconcileInFlight = null;
  }
}

export function clearStaleHighlightReconcileMarker() {
  try {
    sessionStorage.removeItem(STALE_HIGHLIGHT_RECONCILE_KEY);
  } catch {
    // ignore
  }
}
